import type { ContentCatalog, DraftView, RoundReport } from "@bsim/contracts";
import { formatBp, formatUsdPerShare } from "../format";
import { lowerFirst } from "../format/labels";
import { decisionLabel } from "./decomposition";
import { carrier } from "./yearsAhead";
import { moreYears } from "./yearStory";

/** A policy-rate move of one Fed step (25 bp) over the year is the least that teaches repricing; under it the year shows no repricing lesson. */
const MIN_POLICY_MOVE_BP = 25;
/** Funding costs and asset yields within 3 bp of each other repriced about equally. */
const REPRICED_ALIKE_BP = 3;

/** What a lesson rule reads, built once from the year's report. `drivers` is the status-quo twin's year (the market's effect on a bank like yours); null when the report predates it. */
interface LessonFacts {
  readonly roundNo: number;
  readonly arrived: readonly string[];
  readonly changedGrowth: boolean;
  readonly drivers: { readonly moveBp: number; readonly fundingBp: number; readonly assetBp: number; readonly marginBp: number } | null;
}
interface LessonRule { readonly applies: (f: LessonFacts) => boolean; readonly text: (f: LessonFacts) => string }

/** The lesson of one card, for the year it arrived (OR-30.32): worded to hold in every storyline the card can come in. */
const EVENT_LESSONS: Readonly<Record<string, (f: LessonFacts) => string>> = {
  "EV-01": () => "When depositors run, Marked CET1 and the cash and securities you can raise at once are what count; selling bonds turns a paper loss into a real one.",
  "EV-02": (f) => `Office losses were in the book before the appraisals said so${f.changedGrowth ? "; the size of a loss like this was decided partly when you chose how fast to grow" : ""}.`,
  "EV-03": () => OFFER_LESSON,
  "EV-04": () => OFFER_LESSON,
};
const OFFER_LESSON = "Before judging an offer or a letter, you need to know what your bank is worth on its own plan.";

/** The round's own lesson where the guide has one that holds for any player (worded as a conditional when it is about a card that may not have come). */
const ROUND_LESSONS: Readonly<Record<number, string>> = {
  1: "Growing pays only if the new loans earn more than shareholders expect, after the losses you must set aside the day you lend. Standing still is a choice too. You started with spare capital: lend it or give it back.",
  4: "If a run had come, the lesson is this: when depositors run, Marked CET1 and the cash and securities you can raise at once are what count; selling bonds turns a paper loss into a real one.",
};

/** A margin move this far from the asset-yield-less-funding-cost gap gets the note that the margin is earned on the whole book. */
const MARGIN_GAP_NOTE_BP = 3;

const bp = (v: number): string => formatBp(Math.round(Math.abs(v)));

/** The repricing lesson: which side of the balance sheet repriced faster over the year, and what that did to the margin, for a bank like yours (the status-quo twin). Branches on signed numbers only. */
function repricingText(f: LessonFacts): string {
  const core = repricingCore(f);
  const d = f.drivers;
  // The margin is earned on the whole book, so it need not equal the gap between the two rates quoted: said once when the two differ by a visible amount.
  const differs = d !== null && Math.abs(Math.round(d.marginBp) - Math.round(d.assetBp - d.fundingBp)) >= MARGIN_GAP_NOTE_BP;
  return core === "" || !differs ? core : `${core} The margin also reflects the mix of assets and funding, so it need not equal the gap between the two.`;
}
function repricingCore(f: LessonFacts): string {
  const d = f.drivers;
  if (d === null) return "";
  const up = d.moveBp > 0;
  const sign = up ? 1 : -1;
  const funding = sign * d.fundingBp;
  const assets = sign * d.assetBp;
  const lead = `The Fed ${up ? "raised" : "cut"} its rate ${bp(d.moveBp)} over the year. For your status-quo twin (your bank left on its starting settings), on average over the year`;
  const m = Math.round(d.marginBp);
  const margin = (agrees: boolean): string => m === 0 ? "the margin did not move" : `${agrees ? "so" : "even so,"} the margin ${m > 0 ? "rose" : "fell"} ${bp(m)}`;
  const verb = (v: number): string => (v < 0 ? "fell" : "rose");
  const side = (name: string, v: number): string => (Math.round(v) === 0 ? `${name} did not move` : `${name} ${verb(v)} ${bp(v)}${sign * v < 0 ? ` even though the Fed ${up ? "raised" : "cut"} its rate` : ""}`);
  const figures = `${side("funding costs", d.fundingBp)} while ${side("asset yields", d.assetBp)}`;
  if (Math.abs(funding - assets) < REPRICED_ALIKE_BP) return `${lead}, both sides repriced about equally (${figures}), so repricing alone barely moved the margin.`;
  if (up && funding > assets) return `${lead}, ${figures}: deposits priced off the Fed and short funding reprice at once, while most loans and bonds are fixed until they mature. ${cap(margin(m < 0))}${m < 0 ? "; it can recover only as fixed-rate loans and bonds mature and reprice at higher rates" : ""}.`;
  if (up) return `${lead}, ${figures}: deposit rates lag the Fed, and floating-rate loans repriced first, so the lag helped. ${cap(margin(m > 0))}${m > 0 ? "; the gain fades as deposits catch up" : ""}.`;
  if (funding > assets) return `${lead}, ${figures}: indexed and short funding reprice down first. ${cap(margin(m > 0))}.`;
  return `${lead}, ${figures}: retail deposit rates follow a cut only about half as fast as they follow an increase. ${cap(margin(m < 0))}.`;
}
const cap = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

/** Rules in the order they are tried: the card that arrived (OR-30.32), then what the rates did, then the round's own lesson. First that applies wins. */
const RULES: readonly LessonRule[] = [
  { applies: (f) => f.arrived.some((id) => EVENT_LESSONS[id] !== undefined), text: (f) => EVENT_LESSONS[f.arrived.find((id) => EVENT_LESSONS[id] !== undefined) ?? ""]?.(f) ?? "" },
  { applies: (f) => f.drivers !== null && Math.abs(f.drivers.moveBp) >= MIN_POLICY_MOVE_BP, text: repricingText },
  { applies: (f) => ROUND_LESSONS[f.roundNo] !== undefined, text: (f) => ROUND_LESSONS[f.roundNo] ?? "" },
];

function lessonFacts(report: RoundReport): LessonFacts {
  const md = report.attribution.marketDrivers;
  const nim = md?.nim;
  return {
    roundNo: report.roundNo,
    arrived: report.events.filter((e) => e.status !== "cancelled").map((e) => e.card.eventId),
    changedGrowth: (report.decisionAttribution?.rows ?? []).some((r) => typeof r.key !== "string" && /^loans\.[a-z]+\.growth$/.test(r.key.decision)),
    drivers: md === undefined || nim === undefined ? null : { moveBp: md.policyRateChangeBp, fundingBp: md.fundingCostChangeBp, assetBp: md.assetYieldChangeBp, marginBp: nim.changeBp },
  };
}

/**
 * The lesson for a year, built from what happened in it (docs/spec/content.md section 5, "Aha"; the solo player's Results page and final calls page, a facilitated room hears it from its facilitator): the card that arrived if it has a
 * lesson true in every storyline, else a repricing lesson from the year's own policy move, funding-cost and asset-yield changes and margin change, else the round's general lesson. Null when none applies, and no repricing lesson without the twin's figures.
 */
export function yearLesson(report: RoundReport): string | null {
  const f = lessonFacts(report);
  const rule = RULES.find((r) => r.applies(f));
  return rule === undefined ? null : rule.text(f);
}

/**
 * The lesson tied to what the player did (U2-6): the changed decision that carries the most of the years ahead, in the score's own dollars. "You changed commercial property loan growth; the score expects that to add $0.27 over the next two years."
 * Null when no changed decision carries a look-through of five cents or more.
 */
export function lessonPlayerLine(report: RoundReport, catalog: ContentCatalog, draft: DraftView | null = null): string | null {
  const lookThrough = report.score.components?.lookThroughAdjustmentUsdPerShare;
  if (lookThrough === undefined || Math.abs(lookThrough) < MIN_LESSON_USD) return null;
  const top = carrier(report, lookThrough);
  if (top === null || Math.abs(top.v) < MIN_LESSON_USD) return null;
  return `You changed ${lowerFirst(decisionLabel(top.path, catalog, draft))}; the score expects that to ${top.v > 0 ? "add" : "take away"} ${formatUsdPerShare(Math.abs(top.v))} over the next ${moreYears(report).replace(/ more /, " ")}.`;
}
/** Under five cents a share a decision's years ahead are not a lesson. */
const MIN_LESSON_USD = 0.05;
